import { Box, Typography } from '@mui/material'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { usePlanningStore } from '@planning/AppProvider'
import { DeleteConfirmationBody } from '@planning/components/DeleteConfirmationBody'
import { ContainerJourneyStatus } from '@planning/components/molecules/ContainerJourneyStatusChip'
import { useOrderNavigation } from '@planning/hooks/useOrderNavigation'
import { OrderNotesSubForm } from '@planning/pages/Order/V2/organisms/OrderNotesSubForm'
import { BulkEditOrderNotesFormStore } from '@planning/pages/VesselVisit/Stores/BulkEditOrderNotesFormStore'
import { adminActionService, containerService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { TomTableActionsProps } from '@tom-ui/ui'
import { MRT_Row } from 'material-react-table'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { RailVisitTableOrderItemDto } from './useRailVisitTableOrderItemColumns'

export interface BulkEditSelectionDto {
  orderId: number
  containerJourneyId?: number
  containerNumber?: string
}

const getRowIdAsNumber = (rowId: string | number): number => {
  if (typeof rowId === 'number') {
    return rowId
  }

  const parsed = parseInt(rowId)
  if (isNaN(parsed)) {
    throw new Error(`Invalid row id: ${rowId}`)
  }
  return parsed
}

const getRowData = (row: MRT_Row<RailVisitTableOrderItemDto>) => {
  const order = row.original.originalOrder

  return {
    id: order?.id ?? getRowIdAsNumber(row.original.id),
    containerJourneyId: order?.containerJourney?.id,
    number: row.original.containerNo,
    status: order?.containerJourney?.status,
    category: order?.containerJourney?.data?.category,
    direction: order?.data.direction,
  }
}

const partitionVisitsByStatus = (rows: MRT_Row<RailVisitTableOrderItemDto>[]) => {
  const editableVisits: MRT_Row<RailVisitTableOrderItemDto>[] = []
  const departedVisits: MRT_Row<RailVisitTableOrderItemDto>[] = []

  for (const row of rows) {
    const { status } = getRowData(row)
    if (status === ContainerJourneyStatus.Departed) {
      departedVisits.push(row)
    } else {
      editableVisits.push(row)
    }
  }

  return { editableVisits, departedVisits }
}

const getBulkEditData = (editableVisits: MRT_Row<RailVisitTableOrderItemDto>[]) => {
  const bulkEditSelection: BulkEditSelectionDto[] = []

  editableVisits.forEach(row => {
    const { id, containerJourneyId, number } = getRowData(row)
    bulkEditSelection.push({
      orderId: id,
      containerJourneyId: containerJourneyId,
      containerNumber: number,
    })
  })

  return bulkEditSelection
}

export const useRailVisitTableActions = () => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const { openEditOrder } = useOrderNavigation()
  const { appViewStore } = usePlanningStore()

  const orderNotesFormStore = useMemo(() => new BulkEditOrderNotesFormStore(), [])
  const isBulkEditEnabled = useBooleanFlagValue('rail-bulk-edit', false)

  const navigateToBulkEdit = useCallback(
    (editableVisits: MRT_Row<RailVisitTableOrderItemDto>[], category: string) => {
      const bulkEditData = getBulkEditData(editableVisits)
      navigate('bulk-edit', {
        state: {
          data: bulkEditData,
          category,
        },
      })
    },
    [navigate],
  )

  const handleMixedSelection = useCallback(
    async (
      editableVisits: MRT_Row<RailVisitTableOrderItemDto>[],
      departedVisits: MRT_Row<RailVisitTableOrderItemDto>[],
      category: string,
    ) => {
      const title = t('proceedWithIncompleteOnly', 'Proceed with incomplete visits only?')
      const MessageComponent = (
        <Box>
          <Typography variant='body1' gutterBottom>
            {t(
              'editingNotAllowedForDeparted',
              'Editing is no longer allowed for the following departed container visits:',
            )}
          </Typography>
          <Box sx={{ pl: 3, pt: 1 }}>
            <ul>
              {departedVisits.map(row => (
                <li key={row.original.id}>{row.original.containerNo}</li>
              ))}
            </ul>
          </Box>
          <Typography variant='body1' sx={{ mt: 2 }}>
            {t('messagesWillNotBeSentAgain', 'Messages that were sent will not be sent again.')}
          </Typography>
        </Box>
      )

      const isConfirmed = await appViewStore.setOpenConfirmDialog(
        true,
        MessageComponent,
        title,
        undefined,
        {
          primaryActionText: t('yesContinue', 'Yes, continue'),
          secondaryActionText: t('noCancel', 'No, cancel'),
        },
      )

      if (isConfirmed) {
        navigateToBulkEdit(editableVisits, category)
      }
    },
    [appViewStore, navigateToBulkEdit, t],
  )

  const handleBulkEdit = useCallback(
    async (selectedRows: MRT_Row<RailVisitTableOrderItemDto>[]) => {
      const firstRowData = getRowData(selectedRows[0])
      const singleCategory = firstRowData.direction === 'Inbound' ? 'Import' : 'Export'

      const { editableVisits, departedVisits } = partitionVisitsByStatus(selectedRows)

      if (editableVisits.length > 0 && departedVisits.length === 0) {
        navigateToBulkEdit(editableVisits, singleCategory)
      } else if (editableVisits.length === 0 && departedVisits.length > 0) {
        appViewStore.setShowAlert(
          'error',
          t('visitsAlreadyDeparted', 'Container visits have already been departed.'),
        )
      } else if (editableVisits.length > 0 && departedVisits.length > 0) {
        await handleMixedSelection(editableVisits, departedVisits, singleCategory)
      }
    },
    [appViewStore, handleMixedSelection, navigateToBulkEdit, t],
  )

  const handleSingleEdit = useCallback(
    (selectedRows: MRT_Row<RailVisitTableOrderItemDto>[]) => {
      const order = selectedRows[0].original.originalOrder
      if (order) {
        openEditOrder(order.data)
      }
    },
    [openEditOrder],
  )

  const handleAddNotes = useCallback(
    async (selectedRows: MRT_Row<RailVisitTableOrderItemDto>[]) => {
      const selectedContainerJourneyIds = selectedRows
        .map(row => getRowData(row).containerJourneyId)
        .filter((id): id is number => id !== undefined)

      const isConfirm = await appViewStore.setOpenConfirmDialog(
        true,
        <OrderNotesSubForm formStore={orderNotesFormStore} />,
        t('addNotes', 'Add notes'),
        undefined,
        {
          primaryActionText: t('save', 'Save'),
          secondaryActionText: t('cancel', 'Cancel'),
          maxWidth: 'sm',
        },
      )

      if (isConfirm) {
        const cmd = {
          ids: selectedContainerJourneyIds,
          notes: orderNotesFormStore.data.notes ?? undefined,
          operationalInstructions: orderNotesFormStore.data.operationalInstructions ?? undefined,
        }
        try {
          appViewStore.showLoader()
          await containerService.bulkUpdateContainerVisitNotes(cmd)
          appViewStore.setShowAlert(
            'success',
            t('notesAddedSuccessfully', 'Notes added successfully'),
          )
        } catch {
          appViewStore.setShowAlert('error', t('failedToAddNotes', 'Failed to add notes'))
        } finally {
          appViewStore.hideLoader()
        }
      }
    },
    [appViewStore, orderNotesFormStore, t],
  )

  const handleBulkDelete = useCallback(
    async (selectedRows: MRT_Row<RailVisitTableOrderItemDto>[]) => {
      const deletableRows: MRT_Row<RailVisitTableOrderItemDto>[] = []
      const nonDeletableStatusCounts: { [key: string]: number } = {}

      for (const row of selectedRows) {
        const { status } = getRowData(row)
        if (status === ContainerJourneyStatus.Expected) {
          deletableRows.push(row)
        } else {
          const statusLabel = status ?? t('unknownStatus', 'Unknown Status')
          nonDeletableStatusCounts[statusLabel] = (nonDeletableStatusCounts[statusLabel] || 0) + 1
        }
      }

      if (deletableRows.length === 0) {
        appViewStore.setShowAlert(
          'error',
          t('noneCanBeDeletedError', "Only containers with 'Expected' status can be deleted."),
        )
        return
      }

      const deletableCount = deletableRows.length
      const title = t('deleteXContainersTitle', 'Delete {count} containers?', {
        count: deletableCount,
      })

      const MessageComponent = (
        <DeleteConfirmationBody
          deletableCount={deletableCount}
          nonDeletableStatusCounts={nonDeletableStatusCounts}
          t={t}
        />
      )

      const isConfirmed = await appViewStore.setOpenConfirmDialog(
        true,
        MessageComponent,
        title,
        undefined,
        {
          primaryActionText: t('yesDelete', 'Yes, delete'),
          secondaryActionText: t('noKeepContainers', 'No, keep containers'),
        },
      )

      if (!isConfirmed) return

      try {
        appViewStore.showLoader()
        const idsToDelete = deletableRows.map(row => getRowData(row).id)
        await adminActionService.deleteByIds(idsToDelete)
        appViewStore.setShowAlert(
          'success',
          t('successfullyDeletedOrders', 'Deleted successfully.'),
        )
      } catch {
        appViewStore.setShowAlert('error', t('failedToDeleteOrders', 'Failed to delete containers'))
      } finally {
        appViewStore.hideLoader()
      }
    },
    [appViewStore, t],
  )

  return useCallback(
    (
      selectedRows: MRT_Row<RailVisitTableOrderItemDto>[],
    ): TomTableActionsProps<RailVisitTableOrderItemDto>[] => {
      const actions: TomTableActionsProps<RailVisitTableOrderItemDto>[] = []
      const selectionCount = selectedRows.length

      if (selectionCount === 1) {
        actions.push({
          label: t('edit', 'Edit'),
          grouped: true,
          onClick: () => handleSingleEdit(selectedRows),
          testId: 'single-edit-action',
        })
      } else if (selectionCount > 1 && isBulkEditEnabled) {
        actions.push({
          label: t('bulkEdit', 'Bulk edit'),
          grouped: true,
          onClick: () => handleBulkEdit(selectedRows),
          testId: 'bulk-edit-action',
        })
      }

      if (selectionCount > 0) {
        actions.push({
          label: t('addNotes', 'Add notes'),
          grouped: true,
          onClick: () => handleAddNotes(selectedRows),
          testId: 'add-notes-action',
        })

        actions.push({
          label: t('delete', 'Delete'),
          grouped: true,
          onClick: () => handleBulkDelete(selectedRows),
          testId: 'bulk-delete-action',
          disabled: selectedRows.some(
            r => getRowData(r).status !== ContainerJourneyStatus.Expected,
          ),
        })
      }

      return actions
    },
    [t, handleSingleEdit, handleBulkEdit, handleAddNotes, handleBulkDelete],
  )
}
